import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Pagination from '@mui/material/Pagination';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { clearErrors, getProducts } from '../../actions/productAction';
import Loader from '../Layouts/Loader';
import Product from './Product';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MetaData from '../Layouts/MetaData';
import { useLocation } from 'react-router-dom';
import { useLang, uiText, catName } from '../../utils/i18n';
import AgeVerificationModal from './AgeVerificationModal';

// Walks the category tree collecting every category ID that requires age
// verification — a flagged category's own ID, plus every descendant
// beneath it, so admin only has to flag "Alcohol" once.
const collectRestrictedIds = (nodes, parentRestricted = false, out = new Set()) => {
    nodes.forEach((node) => {
        const restricted = parentRestricted || !!node.requiresAgeVerification;
        if (restricted) out.add(node._id);
        if (node.children && node.children.length > 0) {
            collectRestrictedIds(node.children, restricted, out);
        }
    });
    return out;
};

// Normalizes a category name to sentence case, regardless of how it was
// originally typed/migrated ("DIWALI PRODUCTS" -> "Diwali products").
const toSentenceCase = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Flattens the nested category tree into a single list with a depth
// value, so every category AND subcategory is selectable in the filter,
// with indentation showing the hierarchy.
const flattenCategoryTree = (nodes, depth = 0, out = []) => {
    nodes.forEach((node) => {
        out.push({ _id: node._id, name: node.name, nameZh: node.nameZh, slug: node.slug, depth });
        if (node.children && node.children.length > 0) {
            flattenCategoryTree(node.children, depth + 1, out);
        }
    });
    return out;
};

const Products = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();
    const location = useLocation();
    const { lang } = useLang();

    const [price, setPrice] = useState([0, 200000]);

    const initialCategory = new URLSearchParams(location.search).get('category') || "";
    const initialBrand = new URLSearchParams(location.search).get('brand') || "";
    const [selectedCategories, setSelectedCategories] = useState(initialCategory ? initialCategory.split(',') : []);
    const [selectedBrands, setSelectedBrands] = useState(initialBrand ? initialBrand.split(',') : []);
    const section = new URLSearchParams(location.search).get('section') || "";

    const [realCategories, setRealCategories] = useState([]);
    const [realBrands, setRealBrands] = useState([]);
    const [restrictedCategoryIds, setRestrictedCategoryIds] = useState(new Set());
    const [ageConfirmed, setAgeConfirmed] = useState(false);

    // Re-checks age verification any time the customer switches to a
    // different category/brand — not just on the very first page load —
    // so browsing from a normal category into Alcohol still triggers it.
    useEffect(() => {
        setAgeConfirmed(false);
    }, [selectedCategories, selectedBrands]);
    const [categorySearch, setCategorySearch] = useState("");
    const [brandSearch, setBrandSearch] = useState("");

    // pagination
    const [currentPage, setCurrentPage] = useState(1);

    // filter toggles
    const [categoryToggle, setCategoryToggle] = useState(true);
    const [brandToggle, setBrandToggle] = useState(true);

    const { products, loading, error, productsCount, resultPerPage, filteredProductsCount } = useSelector((state) => state.products);
    const keyword = params.keyword;

    const priceHandler = (e, newPrice) => {
        setPrice(newPrice);
    }

    const toggleCategory = (slug) => {
        setSelectedCategories((prev) =>
            prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
        );
    };

    const toggleBrand = (name) => {
        setSelectedBrands((prev) =>
            prev.includes(name) ? prev.filter((b) => b !== name) : [...prev, name]
        );
    };

    const clearFilters = () => {
        setPrice([0, 200000]);
        setSelectedCategories([]);
        setSelectedBrands([]);
    }

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        dispatch(getProducts(keyword, selectedCategories.join(','), price, 0, currentPage, section, selectedBrands.join(',')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, keyword, selectedCategories, selectedBrands, price, currentPage, error, enqueueSnackbar]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                setRealCategories(flattenCategoryTree(data.categories));
                setRestrictedCategoryIds(collectRestrictedIds(data.categories));
            } catch (err) {
                console.error('Failed to load categories', err);
            }
        };
        fetchCategories();

        const fetchBrands = async () => {
            try {
                const { data } = await axios.get('/api/v1/brands');
                setRealBrands(data.brands);
            } catch (err) {
                console.error('Failed to load brands', err);
            }
        };
        fetchBrands();
    }, []);

    const visibleCategories = realCategories.filter((cat) =>
        cat.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
    const visibleBrands = realBrands.filter((b) =>
        b.toLowerCase().includes(brandSearch.toLowerCase())
    );

    const activeFilterChips = [
        ...selectedCategories.map((slug) => ({
            key: `cat-${slug}`,
            label: (() => {
                const found = realCategories.find((c) => c.slug === slug);
                return (lang === 'zh' ? catName(found, lang) : toSentenceCase(found?.name)) || slug;
            })(),
            onDelete: () => toggleCategory(slug),
        })),
        ...selectedBrands.map((name) => ({
            key: `brand-${name}`,
            label: name,
            onDelete: () => toggleBrand(name),
        })),
    ];

    // True whenever at least one product currently shown belongs to a
    // category (or subcategory) flagged as requiring age verification.
    const needsAgeVerification = products.some((p) =>
        (p.categories || []).some((catId) => restrictedCategoryIds.has(catId?._id || catId))
    );

    return (
        <>
            <MetaData title="All Products | 1313 Mart" />

            {needsAgeVerification && !ageConfirmed && (
                <AgeVerificationModal onConfirm={() => setAgeConfirmed(true)} />
            )}

            <main className="w-full mt-14 sm:mt-0" style={needsAgeVerification && !ageConfirmed ? { filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' } : undefined}>

                {/* <!-- row --> */}
                <div className="flex gap-3 mt-2 sm:mt-2 sm:mx-3 m-auto mb-7">

                    {/* <!-- sidebar column  --> */}
                    <div className="hidden sm:flex flex-col w-1/5 px-1">

                        {/* <!-- nav tiles --> */}
                        <div className="flex flex-col bg-white rounded-sm shadow">

                            {/* <!-- filters header --> */}
                            <div className="flex items-center justify-between gap-5 px-4 py-2 border-b">
                                <p className="text-lg font-medium">{uiText('filtersWord', lang)}</p>
                                <span className="uppercase text-primary-blue text-xs cursor-pointer font-medium" onClick={() => clearFilters()}>{uiText('clearAll', lang)}</span>
                            </div>

                            {/* active filters */}
                            {activeFilterChips.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 px-4 py-3 border-b">
                                    {activeFilterChips.map((chip) => (
                                        <Chip
                                            key={chip.key}
                                            label={chip.label}
                                            size="small"
                                            onDelete={chip.onDelete}
                                            sx={{ maxWidth: "100%" }}
                                        />
                                    ))}
                                </div>
                            )}
                            {/* active filters */}

                            <div className="flex flex-col gap-2 py-3 text-sm overflow-hidden">

                                {/* price slider filter */}
                                <div className="flex flex-col gap-2 border-b px-4">
                                    <span className="font-medium text-xs">{uiText('priceLabel', lang)}</span>

                                    <Slider
                                        value={price}
                                        onChange={priceHandler}
                                        valueLabelDisplay="auto"
                                        getAriaLabel={() => 'Price range slider'}
                                        min={0}
                                        max={200000}
                                    />

                                    <div className="flex gap-3 items-center justify-between mb-2 min-w-full">
                                        <span className="flex-1 border px-4 py-1 rounded-sm text-gray-800 bg-gray-50">HK${price[0].toLocaleString()}</span>
                                        <span className="font-medium text-gray-400">{uiText('toWord', lang)}</span>
                                        <span className="flex-1 border px-4 py-1 rounded-sm text-gray-800 bg-gray-50">HK${price[1].toLocaleString()}</span>
                                    </div>
                                </div>
                                {/* price slider filter */}

                                {/* category filter */}
                                <div className="flex flex-col border-b px-4">

                                    <div className="flex justify-between cursor-pointer py-2 pb-4 items-center" onClick={() => setCategoryToggle(!categoryToggle)}>
                                        <p className="font-medium text-xs uppercase">{uiText('categoryLabel', lang)}</p>
                                        {categoryToggle ?
                                            <ExpandLessIcon sx={{ fontSize: "20px" }} /> :
                                            <ExpandMoreIcon sx={{ fontSize: "20px" }} />
                                        }
                                    </div>

                                    {categoryToggle && (
                                        <div className="flex flex-col pb-2 gap-2">
                                            <TextField
                                                size="small"
                                                placeholder={uiText('searchCategoriesPlaceholder', lang)}
                                                value={categorySearch}
                                                onChange={(e) => setCategorySearch(e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ fontSize: "18px" }} className="text-gray-400" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <div className="flex flex-col max-h-64 overflow-y-auto">
                                                <FormControl>
                                                    <FormGroup>
                                                        {visibleCategories.map((cat) => (
                                                            <FormControlLabel
                                                                key={cat._id}
                                                                sx={{ ml: `${cat.depth * 14}px` }}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={selectedCategories.includes(cat.slug)}
                                                                        onChange={() => toggleCategory(cat.slug)}
                                                                    />
                                                                }
                                                                label={<span className="text-sm">{lang === 'zh' ? catName(cat, lang) : toSentenceCase(cat.name)}</span>}
                                                            />
                                                        ))}
                                                        {visibleCategories.length === 0 && (
                                                            <span className="text-xs text-gray-400 py-2">{uiText('noCategoriesMatch', lang)} "{categorySearch}"</span>
                                                        )}
                                                    </FormGroup>
                                                </FormControl>
                                            </div>
                                        </div>
                                    )}

                                </div>
                                {/* category filter */}

                                {/* brand filter */}
                                {realBrands.length > 0 && (
                                    <div className="flex flex-col border-b px-4">

                                        <div className="flex justify-between cursor-pointer py-2 pb-4 items-center" onClick={() => setBrandToggle(!brandToggle)}>
                                            <p className="font-medium text-xs uppercase">{uiText('brandLabel', lang)}</p>
                                            {brandToggle ?
                                                <ExpandLessIcon sx={{ fontSize: "20px" }} /> :
                                                <ExpandMoreIcon sx={{ fontSize: "20px" }} />
                                            }
                                        </div>

                                        {brandToggle && (
                                            <div className="flex flex-col pb-2 gap-2">
                                                <TextField
                                                    size="small"
                                                    placeholder={uiText('searchBrandsPlaceholder', lang)}
                                                    value={brandSearch}
                                                    onChange={(e) => setBrandSearch(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon sx={{ fontSize: "18px" }} className="text-gray-400" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <div className="flex flex-col max-h-64 overflow-y-auto">
                                                    <FormControl>
                                                        <FormGroup>
                                                            {visibleBrands.map((b) => (
                                                                <FormControlLabel
                                                                    key={b}
                                                                    control={
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={selectedBrands.includes(b)}
                                                                            onChange={() => toggleBrand(b)}
                                                                        />
                                                                    }
                                                                    label={<span className="text-sm">{b}</span>}
                                                                />
                                                            ))}
                                                            {visibleBrands.length === 0 && (
                                                                <span className="text-xs text-gray-400 py-2">{uiText('noBrandsMatch', lang)} "{brandSearch}"</span>
                                                            )}
                                                        </FormGroup>
                                                    </FormControl>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                )}
                                {/* brand filter */}

                            </div>

                        </div>
                        {/* <!-- nav tiles --> */}

                    </div>
                    {/* <!-- sidebar column  --> */}

                    {/* <!-- search column --> */}
                    <div className="flex-1">

                        {!loading && products?.length === 0 && (
                            <div className="flex flex-col items-center justify-center gap-3 bg-white shadow-sm rounded-sm p-6 sm:p-16">
                                <h1 className="text-2xl font-medium text-gray-900">{uiText('noResultsExclaim', lang)}</h1>
                                <p className="text-xl text-center text-primary-grey">{uiText('checkSpellingNote', lang)}</p>
                            </div>
                        )}

                        {loading ? <Loader /> : (
                            <div className="flex flex-col gap-2 pb-4 justify-center items-center w-full overflow-hidden bg-white">

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-2 sm:gap-x-0 w-full place-content-start overflow-hidden pb-4 border-b">
                                    {products?.map((product) => (
                                            <Product {...product} key={product._id} />
                                        ))
                                    }
                                </div>
                                {filteredProductsCount > resultPerPage && (
                                    <Pagination
                                        count={Number(((filteredProductsCount + 6) / resultPerPage).toFixed())}
                                        page={currentPage}
                                        onChange={(e, val) => setCurrentPage(val)}
                                        color="primary"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    {/* <!-- search column --> */}
                </div >
                {/* <!-- row --> */}

            </main >
        </>
    );
};

export default Products;